import React, { useState, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { IconButton, Divider, Headline } from '@oneaudi/unified-web-components';
import { useI18n } from '@oneaudi/i18n-context';
import mapContext from '../../../contexts/map-context';
import { ActionTypes } from '../../../contexts/reducers/map-reducer';
import { messages } from '../../../i18n/messages';
import { CenterPanel } from './CenterPanel';
import { LegalDisclaimer } from '../../common/LegalDisclaimer/LegalDisclaimer';
import { CustomerReviewList } from './CustomerReviewList';
import { ServiceSelectionBar } from './ServiceSelectionBar';
import { RatingBreakdown } from './RatingBreakdown';
import { FilterSortBar } from './FilterSortBar';

enum Animation {
  NONE = '',
  SLIDEIN = 'desktopSlidein',
  SLIDEOUT = 'desktopSlideout',
}

export const DesktopFlyout = () => {
  const { ratingSummary, dealerReviews } = mapContext.useMapState();
  const [animation, setAnimation] = useState(Animation.NONE);
  const dispatch = mapContext.useMapDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLDivElement>(null);
  const heading = useRef<HTMLDivElement>(null);

  const ratingLabel = useI18n(messages.five_star_ratings);

  React.useEffect(() => {
    // eslint-disable-next-line
    const handleClick = (event: any) => {
      if (
        (containerRef.current && !containerRef.current.contains(event.target)) ||
        closeRef.current?.contains(event.target)
      ) {
        closeModal();
      }
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [containerRef]);

  const closeModal = () => {
    setAnimation(Animation.SLIDEOUT);
    setTimeout(() => {
      dispatch({ type: ActionTypes.setIsFlyoutOpen, payload: false });
      dispatch({ type: ActionTypes.setReviewCategoryFilter, payload: 'ALL' });
      dispatch({ type: ActionTypes.setReviewStarFilter, payload: 'All stars' });
      dispatch({ type: ActionTypes.setReviewSort, payload: 'Newest' });
    }, 300);
  };

  useEffect(() => {
    setAnimation(Animation.SLIDEIN);
  }, []);

  const totalRatings = ratingSummary?.totalRatings;

  return (
    <DesktopFlyoutContainer ref={containerRef} animation={animation}>
      <Header ref={heading}>
        <SubHeader>
          <RatingsText tag="span" variant={{ order: '4', type: 'headline', weight: 'bold' }}>
            {totalRatings} {ratingLabel}
          </RatingsText>
          <LegalDisclaimer />
        </SubHeader>
        <CloseButton
          ref={closeRef}
          variant="tertiary"
          icon="cancel"
          size="md"
          onClick={closeModal}
        />
      </Header>
      <BodyContainer>
        <RatingPanel>
          <CenterPanel />
          <RatingBreakdown />
        </RatingPanel>
        <DividerStyled />
        {dealerReviews && dealerReviews?.length > 0 && (
          <>
            <ServiceSelectionBar />
            <FilterSortBar />
          </>
        )}
        <CustomerReviewList />
      </BodyContainer>
    </DesktopFlyoutContainer>
  );
};

const DesktopFlyoutContainer = styled.div.attrs({ 'data-test-id': 'desktop-flyout-component' })<{
  animation: Animation;
}>`
  position: absolute;
  display: flex;
  flex-direction: column;
  width: 541px;
  height: calc(100% - 30px);
  margin: 15px 0;
  box-sizing: border-box;
  z-index: -1;
  background: #ffffff;
  animation: ${(props) => props.animation} cubic-bezier(0.21, 0.55, 0.43, 1) 0.2s forwards;

  @keyframes desktopSlidein {
    from {
      width: 353px;
      left: 0;
    }
    to {
      width: clamp(353px, 46vw, 541px);
      left: 395px;
    }
  }

  @keyframes desktopSlideout {
    from {
      width: clamp(353px, 46vw, 541px);
      left: 395px;
    }
    to {
      width: 353px;
      left: 0;
    }
  }
`;

const Header = styled.div`
  padding: 28px 32px 24px;
  display: grid;
  grid-template-columns: auto min-content;
  justify-content: space-between;
  align-items: center;
`;

const BodyContainer = styled.div`
  box-sizing: border-box;
  padding: 0 15px 28px 32px;
  overflow-y: scroll;
  overflow-x: hidden;
  scrollbar-color: #b3b3b3 #ffffff;

  &::-webkit-scrollbar {
    width: 16px;
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    border: 4px solid #ffffff;
    background-color: #b3b3b3;
  }
`;

const CloseButton = styled(IconButton)`
  min-width: auto;
  height: auto;
  padding: 0;
`;

const RatingPanel = styled.div`
  display: grid;
  grid-template-columns: min-content auto;
  column-gap: 18px;
`;

const SubHeader = styled.div`
  display: grid;
  grid-template-columns: auto min-content;
  align-items: center;
`;

const RatingsText = styled(Headline)`
  font-size: 18px;
`;

const DividerStyled = styled(Divider)`
  margin: 32px 0;
`;
